import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import ToolCard from '../components/ToolCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import SEOHead from '../components/SEOHead';
import { Search, Brain, BarChart3, Megaphone, Code2, ListTodo, Image, Grid3X3 } from 'lucide-react';
import { motion } from 'framer-motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  { id: 'all', name: 'All Tools', icon: Grid3X3 },
  { id: 'ai', name: 'AI Tools', icon: Brain },
  { id: 'seo', name: 'SEO Tools', icon: BarChart3 },
  { id: 'marketing', name: 'Marketing', icon: Megaphone },
  { id: 'developer', name: 'Developer', icon: Code2 },
  { id: 'productivity', name: 'Productivity', icon: ListTodo },
  { id: 'image', name: 'Image Tools', icon: Image },
];

const ToolsDirectory = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  
  const activeCategory = searchParams.get('category') || 'all';

  useEffect(() => {
    const fetchTools = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/tools`, {
          params: { category: activeCategory !== 'all' ? activeCategory : undefined }
        });
        setTools(response.data);
      } catch (error) {
        console.error('Error fetching tools:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTools();
  }, [activeCategory]);

  const filteredTools = tools.filter(tool =>
    tool.name.toLowerCase().includes(search.toLowerCase()) ||
    tool.short_description.toLowerCase().includes(search.toLowerCase())
  );

  const handleCategoryChange = (categoryId) => {
    if (categoryId === 'all') {
      searchParams.delete('category');
    } else {
      searchParams.set('category', categoryId);
    }
    setSearchParams(searchParams);
  };

  const categoryName = categories.find(c => c.id === activeCategory)?.name || 'All Tools';
  const seoTitle = activeCategory === 'all' 
    ? 'Free Online Tools - Developer, SEO, AI & Productivity Tools'
    : `Free ${categoryName} - Online Tools | ToolsForge`;
  const seoDescription = activeCategory === 'all'
    ? 'Browse 180+ free online tools for developers, marketers, and designers. JSON formatter, password generator, SEO tools, crypto calculators, and more. No signup required.'
    : `Discover free ${categoryName.toLowerCase()} to boost your productivity. Professional-grade utilities, no signup required.`;

  return (
    <>
      <SEOHead
        title={seoTitle}
        description={seoDescription}
        keywords={`${categoryName.toLowerCase()}, free tools, online tools, web utilities, developer tools`}
        canonical={`${window.location.origin}/tools${activeCategory !== 'all' ? `?category=${activeCategory}` : ''}`}
      />
      <div className="min-h-screen pt-24 pb-16 px-6" data-testid="tools-directory">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4 font-['Plus_Jakarta_Sans']">
            Tools Directory
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Browse our collection of powerful tools designed to boost your productivity
          </p>
        </motion.div>

        {/* Search */}
        <div className="max-w-md mx-auto mb-8">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search tools..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 h-12 bg-muted/50 border-border focus:border-primary"
              data-testid="tools-search"
            />
          </div>
        </div>

        {/* Categories */}
        <div className="flex flex-wrap justify-center gap-2 mb-12" data-testid="category-filters">
          {categories.map((category) => (
            <Button
              key={category.id}
              variant={activeCategory === category.id ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleCategoryChange(category.id)}
              className={`category-pill ${activeCategory === category.id ? 'active' : ''}`}
              data-testid={`filter-${category.id}`}
            >
              <category.icon className="w-4 h-4 mr-2" />
              {category.name}
            </Button>
          ))}
        </div>

        {/* Tools Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="p-6 rounded-xl border border-white/5 bg-white/5">
                <Skeleton className="w-12 h-12 rounded-lg mb-4" />
                <Skeleton className="h-6 w-3/4 mb-2" />
                <Skeleton className="h-4 w-full mb-2" />
                <Skeleton className="h-4 w-2/3 mb-4" />
                <Skeleton className="h-6 w-16" />
              </div>
            ))}
          </div>
        ) : filteredTools.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-muted-foreground">No tools found matching your criteria</p>
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
            data-testid="tools-grid"
          >
            {filteredTools.map((tool, index) => (
              <motion.div
                key={tool.tool_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
              >
                <ToolCard tool={tool} />
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>
      </div>
    </>
  );
};

export default ToolsDirectory;
